import type { AppAction } from '../actions';
import type { CreateFromTemplateLibraryAction } from '../actions/workspace';
import { DEFAULT_WIDGET_ACTIVE_BORDER_COLOR } from '../defaults/workspace';
import type { WorkspaceViewModel } from '../types';

export interface WorkspaceState {
    currentWorkspace: WorkspaceViewModel | null;
    currentWorkspaceStatus: 'initial' | 'pending' | 'saving' | 'error' | 'success';
    currentWorkspaceError: 'permission' | 'generic' | null;
    pendingTemplateConfig: CreateFromTemplateLibraryAction | null;
}

export const initialState: WorkspaceState = {
    currentWorkspace: null,
    currentWorkspaceStatus: 'initial',
    currentWorkspaceError: null,
    pendingTemplateConfig: null,
};

export const UNSAVED_WORKSPACE: WorkspaceViewModel = {
    id: 'new',
    name: 'Untitled Workspace',
    isUnsaved: true,
    isDirty: false,
    isTemplate: false,
    isDefaultWorkspace: false,
    containerIds: [],
    activeContainerId: undefined,
    maximizedContainerId: undefined,
    permissions: {
        effectiveAccess: 'owner',
        effectiveDirectAccess: 'owner',
        canAdmin: true,
        canRead: true,
        canWrite: true,
        doesOwn: true,
        isShared: false,
        isDirectAccessComplex: false,
    },
    layoutSettings: {
        containerLayoutLocked: false,
        gridSnappingEnabled: true,
        enableMosaicLayout: false,
        mosaicGutterPx: 0,
    },
    description: '',
    activeBorderColor: DEFAULT_WIDGET_ACTIVE_BORDER_COLOR,
};

export const workspace = (state = initialState, action: AppAction): WorkspaceState => {
    switch (action.type) {
        case 'setResearchingWorkspace': {
            return {
                ...state,
                currentWorkspaceStatus: 'pending',
                currentWorkspaceError: null,
            };
        }

        case 'completeWorkspaceResearch': {
            return {
                ...state,
                currentWorkspaceStatus: 'success',
            };
        }

        case 'clearWorkspaceState': {
            return { ...initialState };
        }

        case 'initializeUnsavedWorkspace': {
            return {
                ...state,
                currentWorkspace: {
                    ...UNSAVED_WORKSPACE,
                    ...(action.name !== undefined ? { name: action.name } : {}),
                    layoutSettings: {
                        ...UNSAVED_WORKSPACE.layoutSettings,
                        enableMosaicLayout: action.enableMosaicLayout,
                        ...(action.gridSnappingEnabled !== undefined
                            ? { gridSnappingEnabled: action.gridSnappingEnabled }
                            : {}),
                        ...(action.mosaicGutterPx !== undefined ? { mosaicGutterPx: action.mosaicGutterPx } : {}),
                    },
                    mosaicTemplate: action.mosaicTemplate,
                },
                currentWorkspaceStatus: 'success',
                currentWorkspaceError: null,
            };
        }

        case 'setWorkspaceError': {
            return {
                ...state,
                currentWorkspaceStatus: 'error',
                currentWorkspaceError: action.errorType,
            };
        }

        case 'initializeWorkspace': {
            return {
                ...state,
                currentWorkspace: action.workspace,
            };
        }

        case 'addContainer': {
            const currentWorkspace = state.currentWorkspace!;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                    containerIds: [...(currentWorkspace.containerIds ?? []), action.container.id],
                },
            };
        }

        case 'removeContainer': {
            const currentWorkspace = state.currentWorkspace!;
            const wasMaximizedContainer = action.containerId === currentWorkspace.maximizedContainerId;

            // Unmark template slot if the removed container was placed in one
            let mosaicTemplate = currentWorkspace.mosaicTemplate;
            if (mosaicTemplate) {
                const slotEntry = Object.entries(mosaicTemplate.slotContainerMap).find(
                    ([, cId]) => cId === action.containerId,
                );
                if (slotEntry) {
                    const [slotId] = slotEntry;
                    const { [slotId]: _, ...restMap } = mosaicTemplate.slotContainerMap;
                    mosaicTemplate = {
                        ...mosaicTemplate,
                        occupiedSlotIds: mosaicTemplate.occupiedSlotIds.filter((id) => id !== slotId),
                        slotContainerMap: restMap,
                    };
                }
            }

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                    containerIds: (currentWorkspace.containerIds ?? []).filter((x) => x !== action.containerId),
                    ...(wasMaximizedContainer ? { maximizedContainerId: undefined } : {}),
                    mosaicTemplate,
                },
            };
        }

        /** just set the workspace to dirty */
        case 'updateWidget':
        case 'moveTab':
        case 'removeTagFromWatchList':
        case 'addTagToWatchList':
        case 'updateActiveTab':
        case 'removeTab':
        case 'addTab':
        case 'updateTabName':
        case 'setSelectedUniverse':
        case 'userSetNewsStrategy':
        case 'userSetNewsUniverse':
        case 'userSetNewsSymbolFilter':
        case 'onTabChannelChange':
        case 'updateWorkspaceLayout':
        case 'updateMultipleWorkspaceLayouts':
        case 'setAlphaLensChartCollapse':
        case 'setSearchAlphaStrategy':
        case 'setSearchAlphaUniverse':
        case 'setSearchAlphaDisplayingIdeas':
        case 'setSearchAlphaDisplayingResults':
        case 'setSearchAlphaDisplayingSearch':
        case 'storeSearchAlphaSplitPercentage':
        case 'setScreenerIdea':
        case 'setScreenerDisplayingBuilder':
        case 'setScreenerDisplayingResults':
        case 'storeScreenerSplitPercentage':
        case 'setScannerIdea':
        case 'setScannerDisplayingResults':
        case 'setScannerDisplayingBuilder':
        case 'storeScannerSplitPercentage':
        case 'storeScannerUniverseId':
        case 'linkingUpdateTradeProInstrument':
        case 'setLocatesV2AccountIds':
        case 'setLocatesV2AutoAcceptType':
        case 'setLocatesV2AutoAcceptPercent':
        case 'setLocatesV2DisplayingQuote':
        case 'setLocatesV2DisplayingResults':
        case 'setLocatesV2DisplayingCostInfo':
        case 'storeLocatesV2SplitPercentage':
        case 'updateLocatesV2FormData':
        case 'updateLocatesV2QuickSendButtons':
        case 'setLocatesV2SoundAlert':
        case 'setLocatesV2QuantityKilo':
        case 'setLocatesV2UserOnly':
        case 'updateLocatesV2CostInfoColumnTemplate':
        case 'updateLocatesV2Filter':
        case 'updateLocatesV2ColumnWidths':
        case 'updateLocatesV2ColumnOrder':
        case 'updateLocatesV2ColumnVisibility':
        case 'setLocatesV2OnlyPending':
        case 'setLocatesV2OnlyResults':
        case 'updateLocatesV2Sort':
        case 'changeTradingViewChartSymbol':
        case 'setWatchlistIdea':
        case 'updateActiveAlertsTab':
        case 'updateTradingViewChartState': {
            if ('skipDirtyCheck' in action && action.skipDirtyCheck === true) return state;
            const currentWorkspace = state.currentWorkspace;

            if (!currentWorkspace || currentWorkspace.isDirty) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                },
            };
        }

        case 'setSavingTrueAction': {
            return {
                ...state,
                currentWorkspaceStatus: 'saving',
            };
        }

        case 'completedSavingWorkspace': {
            return {
                ...state,
                currentWorkspaceStatus: 'success',
                currentWorkspace: state.currentWorkspace
                    ? {
                          ...state.currentWorkspace,
                          isDirty: false,
                          isUnsaved: false,
                      }
                    : null,
            };
        }

        case 'workspaceSavingFailed': {
            return {
                ...state,
                currentWorkspaceStatus: 'error',
            };
        }

        case 'markWorkspaceDirty': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace || currentWorkspace.isDirty) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                },
            };
        }

        case 'savingCancelledAction': {
            return {
                ...state,
                currentWorkspaceStatus: 'success',
            };
        }

        case 'updateWorkspaceSettings': {
            const currentWorkspace = state.currentWorkspace;

            if (!currentWorkspace) return state;

            const currentlyIsPrimary = currentWorkspace.isDefaultWorkspace;

            const {
                name,
                isPrimaryWorkspace,
                isLocked,
                snappingGridEnabled,
                enableMosaicLayout,
                activeBorderColor,
                mosaicGutterPx,
            } = action.settings;

            const anyChanges =
                currentWorkspace.name !== name ||
                currentlyIsPrimary !== isPrimaryWorkspace ||
                currentWorkspace.layoutSettings?.containerLayoutLocked !== isLocked ||
                currentWorkspace.layoutSettings?.gridSnappingEnabled !== snappingGridEnabled ||
                currentWorkspace.layoutSettings?.enableMosaicLayout !== enableMosaicLayout ||
                currentWorkspace.layoutSettings?.mosaicGutterPx !== mosaicGutterPx ||
                currentWorkspace.activeBorderColor !== activeBorderColor;

            if (!anyChanges) {
                return state;
            }

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    name: action.settings.name,
                    isDirty: false,
                    layoutSettings: {
                        ...currentWorkspace.layoutSettings,
                        containerLayoutLocked: isLocked,
                        gridSnappingEnabled: snappingGridEnabled,
                        enableMosaicLayout: enableMosaicLayout,
                        mosaicGutterPx: mosaicGutterPx,
                    },
                    activeBorderColor: action.settings.activeBorderColor,
                },
            };
        }
        case 'setMaximizedContainer': {
            const currentWorkspace = state.currentWorkspace!;
            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    maximizedContainerId: action.containerId,
                },
            };
        }

        case 'setMosaicTemplate': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    mosaicTemplate: action.template,
                },
            };
        }

        case 'markMosaicTemplateSlotOccupied': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace?.mosaicTemplate) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    mosaicTemplate: {
                        ...currentWorkspace.mosaicTemplate,
                        occupiedSlotIds: [...currentWorkspace.mosaicTemplate.occupiedSlotIds, action.slotId],
                        slotContainerMap: {
                            ...currentWorkspace.mosaicTemplate.slotContainerMap,
                            [action.slotId]: action.containerId,
                        },
                    },
                },
            };
        }

        case 'setLayoutModeLocal': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace) return state;

            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    isDirty: true,
                    layoutSettings: {
                        ...currentWorkspace.layoutSettings,
                        enableMosaicLayout: action.enableMosaicLayout,
                    },
                },
            };
        }

        case 'unmarkMosaicTemplateSlotOccupied': {
            const currentWorkspace = state.currentWorkspace;
            if (!currentWorkspace?.mosaicTemplate) return state;

            const { [action.slotId]: _, ...restMap } = currentWorkspace.mosaicTemplate.slotContainerMap;
            return {
                ...state,
                currentWorkspace: {
                    ...currentWorkspace,
                    mosaicTemplate: {
                        ...currentWorkspace.mosaicTemplate,
                        occupiedSlotIds: currentWorkspace.mosaicTemplate.occupiedSlotIds.filter(
                            (id) => id !== action.slotId,
                        ),
                        slotContainerMap: restMap,
                    },
                },
            };
        }

        case 'setPendingTemplateConfig': {
            return {
                ...state,
                pendingTemplateConfig: action.config,
            };
        }

        case 'clearPendingTemplateConfig': {
            return {
                ...state,
                pendingTemplateConfig: null,
            };
        }

        default:
            return state;
    }
};
